import "./customer-card.scss";

import { RewardBalance } from "@factory/model/query-model-reward";
import { ScanMethod } from "@factory/model/scan-method.enum";
import { useDateTimeContext } from "@shared/utility/date-time-service/date-time.context";
import classNames from "classnames";
import { Divider } from "primereact/divider";
import { useState } from "react";
import Barcode from "react-barcode";
import QRCode from "react-qr-code";

import { appFeature } from "../../../app.feature";
import AnimatedCounter from "../animated-counter/animated-counter";
import CustomerLevel from "../customer-level/customer-level";

interface CustomerCardProps {
  balanceData?: RewardBalance;
  avatar?: string;
  placement?: string;
  hidden?: boolean;
  disableButton?: boolean;
  shouldShowCard?: boolean;
  onClickFn?: () => void;
  onChangeDisplay?: (display: ScanMethod) => void;
}

export function CustomerCard({
  balanceData,
  avatar,
  placement,
  hidden,
  disableButton,
  shouldShowCard,
  onClickFn = () => {},
  onChangeDisplay = () => {},
}: CustomerCardProps) {
  const [displayMode, setDisplayMode] = useState<string>(ScanMethod.QR);
  const points = balanceData?.current_points ?? 0;
  const stamps = balanceData?.current_stamp ?? 0;
  const { formatDate } = useDateTimeContext();
  const { pointsText, stampsText } = appFeature.config.label;

  const toggleDisplay = () => {
    switch (displayMode) {
      case ScanMethod.QR:
        onChangeDisplay(ScanMethod.Barcode);
        setDisplayMode(ScanMethod.Barcode);
        break;
      case ScanMethod.Barcode:
        onChangeDisplay(ScanMethod.QR);
        setDisplayMode(ScanMethod.QR);
        break;
      default:
        onChangeDisplay(ScanMethod.QR);
        setDisplayMode(ScanMethod.QR);
        break;
    }
  };

  if (!balanceData) {
    return (
      <button
        className={` ${hidden ? "bg-none" : "bg-gray-200"}   w-[20%] h-[30vh] ${
          placement === "left" ? "rounded-r-lg" : "rounded-l-lg"
        } flex items-center justify-center`}
        disabled={disableButton}
        onClick={() => {
          onClickFn();
        }}
      ></button>
    );
  }
  return (
    <div
      id="CustomerCard"
      className={`customer-card bg-white shadow-md w-full rounded-lg p-5`}
    >
      <div
        className={classNames(
          shouldShowCard
            ? `flex flex-col-reverse md:flex-row md:grid md:grid-cols-2 items-center`
            : "flex flex-wrap items-center justify-center"
        )}
      >
        <div
          className={classNames(
            "flex flex-col items-center",
            !shouldShowCard ? "w-full" : ""
          )}
        >
          <h1 className="mb-4">
            {balanceData.first_name} {balanceData.last_name}
          </h1>

          {balanceData ? (
            <CustomerLevel avatar={avatar} balance={balanceData} />
          ) : null}

          <div className="flex justify-center gap-4">
            <AnimatedCounter
              from={0}
              to={points}
              duration={1}
              delay={1}
              label={pointsText}
            />
            <Divider layout="vertical" />
            <AnimatedCounter
              from={0}
              to={stamps}
              duration={1}
              delay={1}
              label={stampsText}
            />
          </div>

          <p className="text-xs font-semibold mt-4">
            Card Expiration: {formatDate(balanceData?.expiration_date)}
          </p>
        </div>
        {shouldShowCard ? (
          <div className="flex flex-col">
            <div className="flex justify-end pr-2 pb-4">
              <button className="pi pi-sync" onClick={toggleDisplay}></button>
            </div>
            <div className="flex justify-center bg-white md:p-4 md:min-h-60">
              {displayMode === "qr" && (
                <section>
                  <QRCode value={balanceData?.card_alias || ""} size={200} />
                  <p className="text-xs font-semibold text-center mt-2">
                    {balanceData.card_alias}
                  </p>
                </section>
              )}
              {displayMode === "barcode" && (
                <Barcode value={balanceData?.card_alias || ""} width={1.6} />
              )}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
export default CustomerCard;
